import { Component, DestroyRef, inject, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize, forkJoin, map } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { UploadFileResponse } from 'src/app/core/models/operations';
import { FileUploadResponse } from 'src/app/core/models/operations/operations-file-upload.model';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DeleteFileConfirmationPopupComponent } from '../delete-file-confirmation-popup/delete-file-confirmation-popup.component';
import {
  FILE_UPLOAD_LABELS,
  FileData,
  FileType,
  UploadStatus,
} from './file-upload.constants';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent {
  readonly destroy = inject(DestroyRef);

  readonly CONSTANTS = { ...FILE_UPLOAD_LABELS };
  readonly IMAGES = IMAGE_SRC;
  readonly fileUploadStatus = UploadStatus;
  readonly templateIconUrl = IMAGE_SRC.EXCEL_ACTIVE;
  readonly templateFilePath = this.buildTemplateFilePath();
  readonly templateDownloadName = 'GPD_File_Upload_Template.csv';

  file: FileType = { progress: 0, fileItem: null };
  uploadStatus!: UploadStatus;
  tryAgain = false;
  errorLines: string[] = [];

  // response variables
  acceptedPackages: number = 0;
  acceptedBuildingScans: number = 0;
  acceptedStops: number = 0;
  rejectedPackages: number = 0;
  rejectedBuildingScans: number = 0;
  rejectedStops: number = 0;
  totalNumOfLines: number = 0;
  invalidLines: number = 0;
  message: string[] = [''];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FileData,
    public readonly dialogRef: MatDialogRef<FileUploadComponent>,
    private readonly dialog: MatDialog,
    private readonly summaryService: SummaryService,
    private readonly commonUtils: CommonUtils,
  ) {
    if (this.data?.file) {
      this.file = this.data.file;
      this.uploadStatus = UploadStatus.SUCCESS;
    }
  }

  onFileDropped($event: FileList | never[]) {
    this.file.fileItem = $event[0];
    this.prepareFile();
  }

  fileBrowseHandler($event: Event) {
    this.file.fileItem =
      ($event?.target as HTMLInputElement)?.files?.[0] || null;
    this.prepareFile();
  }

  closeFileUploadPopup() {
    if (this.file.progress === 100) {
      this.cancel();
    }
    this.dialogRef.close();
  }

  async downloadTemplate(event: Event) {
    event.preventDefault();

    try {
      const response = await fetch(this.templateFilePath, {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Unable to download template file.');
      }

      const contentType = response.headers.get('content-type') || '';
      const fileText = await response.text();

      if (
        contentType.includes('text/html') ||
        /<!doctype\s+html|<html[\s>]/i.test(fileText)
      ) {
        throw new Error('Template download returned HTML instead of CSV.');
      }

      const fileBlob = new Blob([fileText], { type: 'text/csv;charset=utf-8' });
      const objectUrl = URL.createObjectURL(fileBlob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = this.templateDownloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
      this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
    }
  }

  private buildTemplateFilePath(): string {
    return new URL('assets/file-upload-template.csv', document.baseURI)
      .toString();
  }

  cancel() {
    const confirmDialog = this.dialog.open(
      DeleteFileConfirmationPopupComponent,
      {
        disableClose: true,
        width: '25rem',
        data: { file: this.file },
      },
    );
    confirmDialog?.afterClosed().subscribe((resp) => {
      if (resp.button === 'CONFIRM') {
        this.dialog.closeAll();
      } else {
        this.dialogRef.close();
      }
    });
  }

  uploadDocument(file: FileType) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = (e.target?.result as string) || '';
      const lines = fileContent.split('\n');
      const chunkSize = 20;

      const chunks: string[] = [];
      const currentChunk: string[] = [];
      const firstSLine = lines.find((line) => line.trim().startsWith('"S"'));
      const firstBLine = lines.find((line) => line.trim().startsWith('"B"'));

      // checking for files with "S"
      if (firstSLine) {
        this.checkForFilesWithS(currentChunk, lines, chunks);
      }

      // checking for building scans; lines starting with "B"
      else if (firstBLine) {
        this.checkForFilesWithB(lines, chunks);
      }

      // for all other cases
      else {
        for (let i = 0; i < lines.length; i += chunkSize) {
          chunks.push(lines.slice(i, i + chunkSize).join('\n'));
        }
      }

      this.uploadChunksConcurrently(chunks);
    };

    reader.readAsText(file.fileItem as File);
  }

  private addToResponse(response: UploadFileResponse) {
    const updateScansAndPackages = (source: {
      packages: any;
      buildingScans: any;
      stops: any;
      invalidLines: any;
      messages?: string[];
      errorLines?: [string];
    }) => {
      this.acceptedBuildingScans += source?.buildingScans?.accepted || 0;
      this.rejectedBuildingScans += source?.buildingScans?.rejected || 0;
      this.acceptedPackages += source?.packages?.accepted || 0;
      this.rejectedPackages += source?.packages?.rejected || 0;
      this.acceptedStops += source?.stops?.accepted || 0;
      this.rejectedStops += source?.stops?.rejected || 0;
      this.invalidLines += source?.invalidLines?.rejected || 0;

      this.totalNumOfLines +=
        source?.packages?.numOfLines +
        source?.buildingScans?.numOfLines +
        source?.stops?.numOfLines +
        source?.invalidLines?.numOfLines;
      this.message = source?.messages ?? [''];
    };

    if (response.response) {
      updateScansAndPackages(response.response);
    } else if (response.error) {
      // updateScansAndPackages(response.error.response);
    }
  }

  private uploadChunksConcurrently(chunks: string[]) {
    this.commonUtils.updateLoadingModal(true);

    const uploadRequests = chunks.map((chunk) => {
      const formData = new FormData();

      const originalName = (this.file.fileItem as File)?.name;

      formData.append(
        'file',
        new Blob([chunk], { type: 'text/plain' }),
        originalName,
      );

      return this.summaryService.uploadFile(formData).pipe(
        map((response: UploadFileResponse) => {
          this.addToResponse(response);

          if (
            response?.code === 200 &&
            (response?.response?.packages?.errorLines.length > 0 ||
              response?.response?.buildingScans?.errorLines.length > 0 ||
              response?.response?.invalidLines?.errorLines.length > 0 ||
              response?.response?.stops?.errorLines.length > 0)
          ) {
            // Error lines for packages
            this.errorLines.push(...response.response.packages?.errorLines);
            // Error lines for building scans
            this.errorLines.push(
              ...response.response.buildingScans?.errorLines,
            );
            // Error lines for stops
            this.errorLines.push(...response.response.stops?.errorLines);
            // Error lines for InvalidLines
            this.errorLines.push(...response.response.invalidLines?.errorLines);
          }
          return response;
        }),
      );
    });

    forkJoin(uploadRequests)
      .pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => {
          const fileUploadResponse: FileUploadResponse = {
            acceptedBuildingScans: this.acceptedBuildingScans,
            acceptedPackages: this.acceptedPackages,
            acceptedStops: this.acceptedStops,
            rejectedStops: this.rejectedStops,
            rejectedBuildingScans: this.rejectedBuildingScans,
            rejectedPackages: this.rejectedPackages,
            totalNumberOfItems: this.totalNumOfLines,
            invalidLines: this.invalidLines,
            message: this.message,
          };
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.updateBannerType(
            BANNER_TYPE.RESULTS_FROM_UPLOAD,
            this.errorLines,
            fileUploadResponse,
          );
          if (this.errorLines.length > 0) {
            const errors = this.errorLines.map((val) => {
              const index = val.trim().indexOf(' ');
              return {
                message: val.substring(index).trim(),
                trackingNumber: val.substring(0, index).trim(),
              };
            });
            this.commonUtils.openDoNotProcessModal(errors);
          }
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(),
        error: () => {
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
        },
      });
  }

  private prepareFile() {
    this.file.progress = 5;
    const progressInterval = setInterval(() => {
      if (this.file.progress === 100) {
        this.uploadStatus = UploadStatus.SUCCESS; // for success
        // this.uploadStatus = UploadStatus.FAILED; // for error
        clearInterval(progressInterval);
      } else {
        this.uploadStatus = UploadStatus.IN_PROGRESS;
        this.file.progress += 5;
      }
    }, 200);
  }

  private checkForFilesWithS(
    currentChunk: string[],
    lines: string[],
    chunks: string[],
  ) {
    for (const line of lines) {
      if (line.startsWith('"S"')) {
        if (currentChunk.length > 0) {
          chunks.push(currentChunk.join('\n'));
        }
        currentChunk = [line];
      } else {
        currentChunk.push(line);
      }
    } // Push the last chunk if it has any lines
    if (currentChunk.length > 0) {
      chunks.push(currentChunk.join('\n'));
    }
  }

  private checkForFilesWithB(lines: string[], chunks: string[]) {
    const buildingScanChunkSize = 15;
    for (let i = 0; i < lines.length; i += buildingScanChunkSize) {
      chunks.push(lines.slice(i, i + buildingScanChunkSize).join('\n'));
    }
  }
}
